/**
 * UpdateAvailableNotice — the «there is a new version» popup of the web apps.
 *
 * A single-page app in a tab that stays open for hours keeps running the bundle it loaded,
 * however many times we deploy meanwhile. This watches for a newer build (the recognition
 * itself is `isNewVersion` & co. in @obrava/shared) and, when one is live, raises a small
 * floating card with an update button. It never reloads on its own — the person may be in
 * the middle of a form — and it can be closed: «avisos mai bloquegen». A closed notice
 * comes back only for a build newer than the one it was closed for.
 *
 * WHEN IT LOOKS
 * -------------
 * Every few minutes while the tab is visible, and again when the person comes back to the
 * tab or the connection returns (at most once a minute). Only when `enabled`: in dev Vite
 * serves modules unbundled and there is no hashed entry to compare.
 *
 * It also listens to Vite's `vite:preloadError`: after a deployment the host drops the old
 * hashed chunks, so a lazy import from a stale tab can fail before the next poll has
 * noticed anything. That is the same remedy — load the new build.
 *
 * Copy and the environment come in as props: this package carries no i18n and cannot read
 * `import.meta.env` (planta's bundler does not take it), so each app's thin wrapper passes
 * its translated strings, `enabled` (`import.meta.env.PROD`) and the `index.html` URL.
 * Token-driven (Light + Dark); touches the DOM only inside effects and handlers, so merely
 * importing it is safe anywhere the barrel is imported.
 */

import React from 'react';
import { entryPathOf, entryScriptFromHtml, isNewVersion } from '@obrava/shared';
import { useTheme } from '../theme/index.js';
import { Button } from './Button.js';

const CHECK_INTERVAL_MS = 5 * 60_000;
/** Floor between two checks, so flicking between tabs does not hammer the host. */
const MIN_GAP_MS = 60_000;
/** Stands in for «the build» when a chunk failed to load before the poll saw a new one. */
const CHUNK_LOAD_FAILED = 'chunk-load-failed';

/** The entry module script of the document the tab is running. */
function entryScriptOfDocument(doc: Document): string | null {
  for (const script of Array.from(doc.querySelectorAll('script[type="module"][src]'))) {
    const src = script.getAttribute('src');
    if (src === null) continue;
    const path = entryPathOf(src, doc.location.href);
    if (path !== null) return path;
  }
  return null;
}

/**
 * The entry script of the `index.html` served right now, read past every cache; `null`
 * when it cannot be told (offline, non-2xx, no entry script in the answer).
 */
async function fetchLatestEntry(indexUrl: string): Promise<string | null> {
  try {
    const response = await fetch(indexUrl, { cache: 'no-store', credentials: 'omit' });
    if (!response.ok) return null;
    return entryScriptFromHtml(await response.text(), window.location.href);
  } catch {
    return null;
  }
}

interface AppUpdate {
  readonly available: boolean;
  readonly dismiss: () => void;
}

function useAppUpdate(enabled: boolean, indexUrl: string): AppUpdate {
  const [latest, setLatest] = React.useState<string | null>(null);
  const [dismissed, setDismissed] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enabled) return;
    const current = entryScriptOfDocument(document);
    if (current === null) return;

    let alive = true;
    let inFlight = false;
    let lastCheck = Date.now();

    const check = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      lastCheck = Date.now();
      try {
        const entry = await fetchLatestEntry(indexUrl);
        if (alive && isNewVersion(current, entry)) setLatest(entry);
      } finally {
        inFlight = false;
      }
    };

    const onReturn = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheck < MIN_GAP_MS) return;
      void check();
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void check();
    }, CHECK_INTERVAL_MS);
    const onChunkError = (): void => {
      setLatest((prev) => prev ?? CHUNK_LOAD_FAILED);
      setDismissed(null);
    };

    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('online', onReturn);
    window.addEventListener('vite:preloadError', onChunkError);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('online', onReturn);
      window.removeEventListener('vite:preloadError', onChunkError);
    };
  }, [enabled, indexUrl]);

  const dismiss = React.useCallback(() => setDismissed(latest), [latest]);
  return { available: latest !== null && latest !== dismissed, dismiss };
}

export interface UpdateAvailableNoticeProps {
  /** Poll for a new build only when true (production builds). */
  readonly enabled: boolean;
  /** Where the host serves the app's `index.html` (e.g. `${BASE_URL}index.html`). */
  readonly indexUrl: string;
  readonly title: string;
  readonly body: string;
  /** Label of the button that reloads the app. */
  readonly actionLabel: string;
  /** Accessible name of the close «×». */
  readonly closeLabel: string;
}

export function UpdateAvailableNotice(props: UpdateAvailableNoticeProps): React.ReactElement | null {
  const theme = useTheme();
  const { available, dismiss } = useAppUpdate(props.enabled, props.indexUrl);

  if (!available) return null;

  const gutter = theme.spacing.space[5];
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: `calc(${gutter}px + env(safe-area-inset-bottom, 0px))`,
        width: `min(460px, calc(100vw - ${2 * gutter}px))`,
        // Above the modal dialogs (100): the notice is small and must stay reachable.
        zIndex: 110,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.space[4],
        padding: `${theme.spacing.space[3]}px ${theme.spacing.space[4]}px`,
        backgroundColor: theme.color.surfaceOverlay,
        border: `1px solid ${theme.color.border}`,
        borderRadius: theme.radii.radius.lg,
        boxShadow: theme.radii.shadow.overlay,
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          style={{
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.footnote,
            fontWeight: theme.typography.weight.semibold,
            color: theme.color.textPrimary,
          }}
        >
          {props.title}
        </span>
        <span
          style={{
            fontFamily: theme.typography.family.sans,
            fontSize: theme.typography.size.footnote,
            lineHeight: theme.typography.lineHeight.normal,
            color: theme.color.textSecondary,
          }}
        >
          {props.body}
        </span>
      </div>
      <Button onClick={() => window.location.reload()}>{props.actionLabel}</Button>
      <button
        type="button"
        aria-label={props.closeLabel}
        onClick={dismiss}
        style={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: theme.color.textTertiary,
          fontFamily: theme.typography.family.sans,
          fontSize: theme.typography.size.body,
          lineHeight: 1,
          padding: theme.spacing.space[1],
        }}
      >
        ×
      </button>
    </div>
  );
}
